import MojitoCore from 'mojito/core';

import {borderStyle} from '#core/utils/style-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';

import {Intents} from '#core/application/intents/index.js';

import {generateInput} from '#core/application/modules/tiles/input/index.js';
import {generateAnimatedButton, generateSecondaryButton} from '#core/application/modules/tiles/button/index.js';
import {CLOSE_BUTTON_TYPES, generateCloseButton} from '#core/application/modules/tiles/close-button/index.js';

import '#core/application/modules/tiles/notification/index.js'; // needed for playground

const merge = MojitoCore.Presentation.uiStyle.merge;

export function loginDialogConfig(PALETTE) {
    const {FONT} = PALETTE;

    const LOGIN_DIALOG_INPUT = generateInput(PALETTE.LOGIN_DIALOG_INPUT);
    const PASSWORD_INPUT_ICON = {
        style: {
            minWidth: '40px',
            minHeight: '38px',
            padding: '10px 12px',
            fill: PALETTE.LOGIN_DIALOG_INPUT.INPUT_ICON_COLOR,
        },
    };
    const PASSWORD_INPUT = {
        style: {
            showHideIconContainer: {
                borderRadius: `0 ${PALETTE.LOGIN_DIALOG_INPUT.INPUT_BORDER_RADIUS}px ${PALETTE.LOGIN_DIALOG_INPUT.INPUT_BORDER_RADIUS}px 0`,
                minHeight: 'auto',
            },
        },
        textInput: LOGIN_DIALOG_INPUT,
        passwordShowIcon: PASSWORD_INPUT_ICON,
        passwordHideIcon: PASSWORD_INPUT_ICON,
    };
    // change password input
    const CHANGE_PASSWORD_INPUT = merge(PASSWORD_INPUT, {
        style: {
            showHideIconContainer: {
                top: 1, // because mojito has a bug, that leads to shifted button
                right: 1,
            },
        },
    });
    const CHANGE_PASSWORD_INPUT_LABEL = {
        fontSize: FONT.SIZE_SMALL,
        ...PALETTE.LOGIN_DIALOG_INPUT.INPUT_LABEL_TEXT,
    };

    return {
        LoginDialogContent: {
            dialogContainer: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    ...PALETTE.LOGIN_DIALOG_CONTAINER_TEXT,
                },
            },
            contentContainer: {
                itemSpacing: 8,
                style: {
                    padding: '24px 16px 32px',
                },
            },
            // username
            usernameLabelContainer: {
                style: {
                    padding: 0,
                },
            },
            usernameLabel: {
                style: PALETTE.LOGIN_DIALOG_INPUT.INPUT_LABEL_TEXT,
            },
            usernameInput: LOGIN_DIALOG_INPUT,
            // password
            passwordLabelContainer: {
                style: {
                    padding: 0,
                },
            },
            passwordLabel: {
                style: PALETTE.LOGIN_DIALOG_INPUT.INPUT_LABEL_TEXT,
            },
            passwordInput: PASSWORD_INPUT,
            logoutReasonContainer: {
                style: {
                    ...resolveBackground(PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_BG),
                    ...borderStyle(
                        PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_BORDER_RADIUS,
                        PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_BORDER_COLOR
                    ),
                    padding: '8px',
                    ...PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_TEXT,
                },
            },
            logoutReasonMessage: {
                container: {
                    padding: 0, // Override mojito default
                },
            },
            errorIcon: {
                style: {
                    display: 'none',
                },
            },
            errorMessageContainer: {
                style: {
                    ...PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_TEXT,
                },
            },
            style: {
                errorContainer: {
                    ...resolveBackground(PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_BG),
                    ...borderStyle(
                        PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_BORDER_RADIUS,
                        PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_BORDER_COLOR
                    ),
                },
            },
            loginViewContainer: {
                style: {
                    padding: '0',
                },
            },
            forgotDetailsText: {
                style: {
                    textAlign: 'center',
                    ...PALETTE.LOGIN_DIALOG_FORGOT_DETAILS_TEXT,
                },
            },
            forgotDetails: {
                hide: !Intents.FORGOT_DETAILS,
                intent: Intents.FORGOT_DETAILS,
            },
            registerContainer: {
                style: {
                    borderWidth: '1px 0 0 0',
                    borderColor: PALETTE.LOGIN_DIALOG_SEPARATOR_COLOR,
                    borderStyle: 'solid',
                    padding: '32px 16px',
                },
            },
            registerHeaderText: {
                style: {
                    ...PALETTE.LOGIN_DIALOG_REGISTER_INVITATION_TEXT,
                },
            },
            loginButton: generateAnimatedButton(PALETTE.LOGIN_DIALOG_LOGIN_BUTTON),
            registerButton: generateSecondaryButton(PALETTE.LOGIN_DIALOG_REGISTER_BUTTON),
            registerButtonIntent: Intents.REGISTER,
        },
        LoginDialog: {
            dialog: {
                background: {
                    ...resolveBackground(PALETTE.LOGIN_DIALOG_CONTAINER_BG),
                },
                header: {
                    container: {
                        style: {
                            _mobile_margin: '10px 0 0',
                            height: 'auto', // override mojito default
                        },
                    },
                    title: {
                        padding: '46px 20px 0px',
                    },
                    titleText: {
                        fontWeight: 'bold',
                        ...PALETTE.LOGIN_DIALOG_TITLE_TEXT,
                    },
                },
                closeButtonContainer: {
                    style: {
                        right: 0,
                        top: 'initial',
                        _mobile_top: 'initial',
                        bottom: 'calc(100% + 8px)',
                        _mobile_bottom: '100%',
                        left: 'initial',
                    },
                },
                closeButton: generateCloseButton(PALETTE.LOGIN_DIALOG_CLOSE_BUTTON, CLOSE_BUTTON_TYPES.CIRCULAR),
            },
            style: {
                tabsContainer: {
                    borderSpacing: '16px 0px',
                    lineHeight: '14px',
                    marginTop: '40px',
                    padding: '0 10px',
                    boxSizing: 'border-box',
                },
                loginTab: {
                    fontSize: FONT.SIZE_MEDIUM,
                    ...PALETTE.LOGIN_DIALOG_LOGIN_METHOD_SELECTOR.PIVOT_TEXT,
                },
                selectedTab: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_BOLD,
                    borderStyle: 'solid',
                    borderWidth: '0px 0px 4px 0px',
                    borderColor: PALETTE.LOGIN_DIALOG_LOGIN_METHOD_SELECTOR.PIVOT_INDICATOR_BG,
                    ...PALETTE.LOGIN_DIALOG_LOGIN_METHOD_SELECTOR.PIVOT_SELECTED_TEXT,
                },
            },
        },
        ChangePasswordView: {
            newPasswordInput: CHANGE_PASSWORD_INPUT,
            temporaryPasswordInput: CHANGE_PASSWORD_INPUT,
            dialog: {
                background: {
                    ...resolveBackground(PALETTE.LOGIN_DIALOG_CONTAINER_BG),
                },
                header: {
                    titleText: {
                        ...PALETTE.LOGIN_DIALOG_TITLE_TEXT,
                    },
                },
            },
            submitButton: generateAnimatedButton(PALETTE.CHANGE_PASSWORD_SUBMIT_BUTTON),
            style: {
                mainContainer: {
                    fontSize: FONT.SIZE_MEDIUM,
                    ...PALETTE.CHANGE_PASSWORD_CONTAINER_TEXT,
                },
                newPasswordPoliciesContainer: {
                    text: {
                        fontSize: FONT.SIZE_MEDIUM,
                        ...PALETTE.CHANGE_PASSWORD_POLICIES_TEXT,
                    },
                },
                errorMessage: {
                    ...resolveBackground(PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_BG),
                    borderRadius: PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_BORDER_RADIUS,
                    borderColor: PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_BORDER_COLOR,
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    fontSize: FONT.SIZE_MEDIUM,
                    ...PALETTE.LOGIN_DIALOG_ERROR_NOTIFICATION.ERROR_TEXT,
                },
                passwordFormContainer: {
                    padding: '24px 0px',
                },
                temporaryPasswordContainer: CHANGE_PASSWORD_INPUT_LABEL,
                newPasswordContainer: CHANGE_PASSWORD_INPUT_LABEL,
                newPasswordConfirmedContainer: CHANGE_PASSWORD_INPUT_LABEL,
                okButton: generateSecondaryButton(PALETTE.CHANGE_PASSWORD_OK_BUTTON).style.base,
            },
        },
    };
}
